import { useInfiniteQuery } from '@tanstack/react-query';
import { findActionIn, invokeAction } from 'actions';
import { useAuthStatus } from 'features/auth';
const DEFAULT_PAGE_SIZE = 50;
/**
 * Paginated list of projects that *can* be assigned to a given agent (i.e.
 * projects not already assigned, and not authored by an agent). Reads the
 * per-record `listAssignableDLProjects` action from the agent's
 * `potentialAction`. The action carries `excludeAssignmentsOfAgentUUID` and
 * `excludeAuthored` as readonly preset properties; we read them off the
 * action and pass them through verbatim when materializing the URL.
 */
export function useListAssignableDLProjects({ agent, searchTerm, status }) {
    const authStatus = useAuthStatus();
    const user = authStatus.kind === 'authenticated' ? authStatus.user : null;
    const action = findActionIn(agent.potentialAction, 'listAssignableDLProjects');
    const agentUUID = agent.UUID || '';
    const sendSearch = searchTerm.length >= 2 ? searchTerm : '';
    const sendStatus = status === 'all' ? '' : status;
    return useInfiniteQuery({
        queryKey: ['listAssignableDLProjects', user?.email, agentUUID, sendSearch, sendStatus],
        enabled: Boolean(user && action),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const view = lastPage.response.filteredView;
            const total = view?.numberOfItems ?? 0;
            const pageSize = view?.pageSize ?? DEFAULT_PAGE_SIZE;
            const current = view?.currentPage ?? lastPage.page;
            if (current * pageSize >= total)
                return undefined;
            return current + 1;
        },
        queryFn: async ({ pageParam }) => {
            if (!user || !action)
                throw new Error('listAssignableDLProjects unavailable');
            // Readonly preset fields baked into the action — pass them through verbatim.
            const excludeAssignmentsOfAgentUUID = typeof action.excludeAssignmentsOfAgentUUID === 'string'
                ? action.excludeAssignmentsOfAgentUUID
                : agentUUID;
            const excludeAuthored = typeof action.excludeAuthored === 'string'
                ? action.excludeAuthored
                : 'True';
            const variables = {
                currentPage: String(pageParam),
                pageSize: String(DEFAULT_PAGE_SIZE),
                excludeAssignmentsOfAgentUUID,
                excludeAuthored,
            };
            if (sendSearch)
                variables.searchTerm = sendSearch;
            if (sendStatus)
                variables.status = sendStatus;
            const response = await invokeAction(action, user.token, { variables });
            if (!response.ok)
                throw new Error(`listAssignableDLProjects returned ${response.status}`);
            const json = (await response.json());
            return { response: json, page: pageParam };
        },
    });
}
